/**
 * Device-bound signing session (PCB external-access standard §3E, Bill 2026-10-07).
 *
 * Verifying the e-mailed code returns a random session for THIS browser tab. Every call to the
 * signing API carries it in `x-signing-session`; the server keeps only its hash. Without it the
 * link alone opens nothing - another device, a new tab or a lapsed session goes back through the
 * e-mail + code (which also restarts the copy-download window). Kept in memory and this tab's
 * sessionStorage (survives a reload, never shared across tabs); storage may be unavailable.
 */
const KEY = 'pcb-signing-session';
let memory: string | null = null;

export function getKioskSession(): string | null {
  if (memory) return memory;
  try {
    memory = window.sessionStorage.getItem(KEY);
  } catch {
    /* storage blocked - memory only */
  }
  return memory;
}

export function setKioskSession(session: string): void {
  memory = session;
  try {
    window.sessionStorage.setItem(KEY, session);
  } catch {
    /* storage blocked - memory only */
  }
}

/** fetch() for the signing API: adds this tab's session when there is one. */
export function kioskFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const session = getKioskSession();
  if (!session) return fetch(url, init);
  const headers = new Headers(init.headers ?? {});
  headers.set('x-signing-session', session);
  return fetch(url, { ...init, headers });
}

/** Drop this tab's session so the next call goes back through the e-mail + code. */
export function clearKioskSession(): void {
  memory = null;
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    /* storage blocked */
  }
}
